/* eslint-disable */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useAuth } from '@/Components/Auth/Login/auth';
import authServices from '@/Services/auth.services';
import businessConfigServices from '@/Services/business-config.services';
import notificationServices from '@/Services/notification.services';
import styles from './BusinessConfigPage.module.scss';

// View and edit one customer's Business_Config row: its columns and the keys inside the configs /
// reportConfigs JSON. Internal users only; the API checks the role and ignores the domain's clientId.
// A save sends only the changed fields with the values loaded here, so the API refuses (instead of
// overwriting) anything someone else changed in the meantime.

const TABS = [
	{ id: 'columns', section: 'column', title: 'Columns', blurb: 'Columns of the Business_Config row.' },
	{ id: 'configs', section: 'configs', title: 'configs JSON', blurb: 'Keys inside the configs JSON column. A save writes only the keys you change.' },
	{ id: 'reportConfigs', section: 'reportConfigs', title: 'reportConfigs JSON', blurb: 'Keys inside the reportConfigs JSON column, read by the reports service.' },
	{ id: 'history', title: 'History' },
];
const SECTION_TITLE = { column: 'Column', configs: 'configs', reportConfigs: 'reportConfigs' };
const CUSTOM_KEY_TYPES = ['bool', 'int', 'number', 'string', 'list', 'json'];
const KEY_PATTERN = /^[A-Za-z_][A-Za-z0-9_]{0,63}$/;

const fieldId = (section, key) => `${section}:${key}`;

// Same value whatever the key order or 10 vs 10.0
function canon(value) {
	const norm = (v) => {
		if (Array.isArray(v)) return v.map(norm);
		if (v && typeof v === 'object') {
			return Object.keys(v).sort().reduce((acc, k) => ({ ...acc, [k]: norm(v[k]) }), {});
		}
		return v === undefined ? null : v;
	};
	return JSON.stringify(norm(value));
}

function formatValue(value, isSet = true) {
	if (!isSet) return 'not set';
	if (value === null || value === undefined) return 'empty';
	if (value === true) return 'On';
	if (value === false) return 'Off';
	if (Array.isArray(value)) return value.length ? value.join(', ') : '[]';
	if (typeof value === 'object') return JSON.stringify(value);
	if (value === '') return '""';
	return String(value);
}

function defaultFor(field) {
	switch (field.type) {
		case 'bool': return false;
		case 'flag01': return 0;
		case 'int':
		case 'number': return 0;
		case 'enum': return field.options?.[0] ?? '';
		case 'list': return [];
		case 'boolMap': return Object.fromEntries((field.fields || []).map((f) => [f, false]));
		case 'json': return {};
		default: return '';
	}
}

// Number and JSON fields are typed as text; the draft keeps the text and its parsed value
function textFor(field, value) {
	if (field.type === 'json') return value === undefined ? '' : JSON.stringify(value, null, 2);
	if (field.type === 'int' || field.type === 'number') return value === null || value === undefined ? '' : String(value);
	return undefined;
}

function parseText(field, text) {
	const trimmed = (text ?? '').trim();
	if (field.type === 'json') {
		if (!trimmed) return { error: 'Enter JSON' };
		try {
			return { value: JSON.parse(trimmed) };
		} catch (e) {
			return { error: 'Not valid JSON' };
		}
	}
	if (!trimmed) return field.nullable ? { value: null } : { error: 'Enter a number' };
	const num = Number(trimmed);
	if (Number.isNaN(num)) return { error: 'Not a number' };
	if (field.type === 'int' && !Number.isInteger(num)) return { error: 'Whole number only' };
	if (field.min != null && num < field.min) return { error: `At least ${field.min}` };
	if (field.max != null && num > field.max) return { error: `At most ${field.max}` };
	return { value: num };
}

function ListEditor({ value, options, onChange, disabled, listId }) {
	const [entry, setEntry] = useState('');
	const items = Array.isArray(value) ? value : [];
	const add = (raw) => {
		const parts = raw.split(',').map((p) => p.trim()).filter(Boolean);
		const next = [...items];
		parts.forEach((p) => { if (!next.includes(p)) next.push(p); });
		if (next.length !== items.length) onChange(next);
		setEntry('');
	};
	const remaining = (options || []).filter((o) => !items.includes(o));
	return (
		<div className={styles.listEditor}>
			<div className={styles.chips}>
				{items.map((item) => (
					<span key={item} className={styles.chip}>
						{item}
						{!disabled && (
							<button type='button' aria-label={`Remove ${item}`} onClick={() => onChange(items.filter((i) => i !== item))}>×</button>
						)}
					</span>
				))}
				{items.length === 0 && <span className={styles.muted}>Empty list</span>}
			</div>
			{!disabled && (
				<div className={styles.listAdd}>
					<input
						type='text'
						list={listId}
						placeholder='Add a value'
						value={entry}
						onChange={(e) => setEntry(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === 'Enter' || e.key === ',') {
								e.preventDefault();
								if (entry.trim()) add(entry);
							}
						}}
						onBlur={() => { if (entry.trim()) add(entry); }}
					/>
					<datalist id={listId}>
						{remaining.map((o) => <option key={o} value={o} />)}
					</datalist>
					{remaining.slice(0, 8).map((o) => (
						<button key={o} type='button' className={styles.suggestion} onClick={() => add(o)}>+ {o}</button>
					))}
				</div>
			)}
		</div>
	);
}

function FieldEditor({ field, draftValue, draftText, onChange, disabled }) {
	const id = `bc-${field.section}-${field.key}`;
	const value = draftValue;
	switch (field.type) {
		case 'bool':
		case 'flag01': {
			const on = field.type === 'flag01' ? 1 : true;
			const off = field.type === 'flag01' ? 0 : false;
			const current = value === null || value === undefined ? '' : (value === on || value === true || value === 1 ? 'on' : 'off');
			return (
				<select
					id={id}
					value={current}
					disabled={disabled}
					onChange={(e) => onChange({ to: e.target.value === '' ? null : (e.target.value === 'on' ? on : off) })}
				>
					{(field.nullable || current === '') && <option value='' disabled={!field.nullable}>Empty</option>}
					<option value='on'>{field.type === 'flag01' ? 'On (1)' : 'On'}</option>
					<option value='off'>{field.type === 'flag01' ? 'Off (0)' : 'Off'}</option>
				</select>
			);
		}
		case 'enum': {
			const options = [...(field.options || [])];
			if (value && !options.includes(value)) options.push(value);
			return (
				<select id={id} value={value ?? ''} disabled={disabled} onChange={(e) => onChange({ to: e.target.value === '' ? null : e.target.value })}>
					{(field.nullable || value == null) && <option value='' disabled={!field.nullable}>Empty</option>}
					{options.map((o) => <option key={o} value={o}>{o}</option>)}
				</select>
			);
		}
		case 'int':
		case 'number':
			return (
				<input
					id={id}
					type='text'
					inputMode='decimal'
					className={styles.numberInput}
					value={draftText ?? ''}
					disabled={disabled}
					placeholder={field.nullable ? 'Empty' : ''}
					onChange={(e) => {
						const parsed = parseText(field, e.target.value);
						onChange({ text: e.target.value, to: parsed.value, error: parsed.error });
					}}
				/>
			);
		case 'list':
			return <ListEditor value={value} options={field.options} disabled={disabled} listId={`${id}-options`} onChange={(next) => onChange({ to: next })} />;
		case 'boolMap': {
			const map = value && typeof value === 'object' ? value : {};
			const keys = [...new Set([...(field.fields || []), ...Object.keys(map)])];
			return (
				<div className={styles.boolMap}>
					{keys.map((k) => (
						<label key={k}>
							<input
								type='checkbox'
								checked={!!map[k]}
								disabled={disabled}
								onChange={(e) => onChange({ to: { ...map, [k]: e.target.checked } })}
							/>
							<span>{k}</span>
						</label>
					))}
				</div>
			);
		}
		case 'json':
			return (
				<textarea
					id={id}
					rows={Math.min(8, Math.max(2, (draftText || '').split('\n').length))}
					className={styles.jsonInput}
					value={draftText ?? ''}
					disabled={disabled}
					spellCheck={false}
					onChange={(e) => {
						const parsed = parseText(field, e.target.value);
						onChange({ text: e.target.value, to: parsed.value, error: parsed.error });
					}}
				/>
			);
		default:
			return (
				<input
					id={id}
					type='text'
					value={value ?? ''}
					disabled={disabled}
					placeholder={field.nullable ? 'Empty' : ''}
					maxLength={field.maxLength || undefined}
					onChange={(e) => onChange({ to: e.target.value })}
				/>
			);
	}
}

export default function BusinessConfigPage() {
	const userPermissions = useSelector((state) => state?.navigation?.userPermissions);
	const { appConfig = {}, setAppConfig } = useAuth();

	const [customers, setCustomers] = useState([]);
	// Always the customer this console is logged in for; the API refuses any other
	const customerId = appConfig?.customerId ?? null;
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(false);
	const [loadFailed, setLoadFailed] = useState(false);
	const [tab, setTab] = useState('configs');
	const [search, setSearch] = useState('');
	const [show, setShow] = useState('all');
	const [drafts, setDrafts] = useState({});
	const [customFields, setCustomFields] = useState([]);
	const [newKey, setNewKey] = useState('');
	const [newKeyType, setNewKeyType] = useState('bool');
	const [reviewOpen, setReviewOpen] = useState(false);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (!userPermissions?.INTERNAL) return;
		// Only used for the "Set for X of N customers" counts
		businessConfigServices.listCustomers().then((res) => {
			setCustomers(res?.data || []);
		}).catch(() => {});
	}, [userPermissions?.INTERNAL]);

	const load = useCallback(async (id) => {
		if (id == null) return;
		setLoading(true);
		setLoadFailed(false);
		try {
			const res = await businessConfigServices.getBusinessConfig(id);
			setData(res?.data || null);
			setDrafts({});
			setCustomFields([]);
		} catch (e) {
			setLoadFailed(true);
			setData(null);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => { load(customerId); }, [customerId, load]);

	// Every field, tagged with its section; columns always count as set
	const fields = useMemo(() => {
		if (!data) return [];
		const withSection = (list, section) => (list || []).map((f) => ({ ...f, section, isSet: section === 'column' ? true : f.isSet }));
		const loadedIds = new Set();
		const all = [
			...withSection(data.columns, 'column'),
			...withSection(data.configs, 'configs'),
			...withSection(data.reportConfigs, 'reportConfigs'),
		];
		all.forEach((f) => loadedIds.add(fieldId(f.section, f.key)));
		return [...all, ...customFields.filter((f) => !loadedIds.has(fieldId(f.section, f.key)))];
	}, [data, customFields]);

	const fieldById = useMemo(() => Object.fromEntries(fields.map((f) => [fieldId(f.section, f.key), f])), [fields]);

	// A draft is { to, text?, error?, remove? }; it counts as a change only when it differs from what was loaded
	const changedIds = useMemo(() => Object.keys(drafts).filter((id) => {
		const d = drafts[id];
		const f = fieldById[id];
		if (!f) return false;
		if (d.remove) return f.isSet;
		if (!f.isSet) return true;
		return d.error ? true : canon(d.to) !== canon(f.value);
	}), [drafts, fieldById]);
	const errorIds = changedIds.filter((id) => drafts[id]?.error);
	const dirty = changedIds.length > 0;

	useEffect(() => {
		if (!dirty) return undefined;
		const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
		window.addEventListener('beforeunload', warn);
		return () => window.removeEventListener('beforeunload', warn);
	}, [dirty]);

	const setDraft = (field, patch) => {
		const id = fieldId(field.section, field.key);
		setDrafts((prev) => ({ ...prev, [id]: { ...(prev[id] || { to: field.value, text: textFor(field, field.value) }), ...patch, remove: false } }));
	};
	const startValue = (field) => {
		const value = defaultFor(field);
		const id = fieldId(field.section, field.key);
		setDrafts((prev) => ({ ...prev, [id]: { to: value, text: textFor(field, value) } }));
	};
	const undo = (field) => {
		const id = fieldId(field.section, field.key);
		setDrafts((prev) => {
			const next = { ...prev };
			delete next[id];
			return next;
		});
		if (field.isCustom) setCustomFields((prev) => prev.filter((f) => fieldId(f.section, f.key) !== id));
	};
	const markRemove = (field) => {
		const id = fieldId(field.section, field.key);
		setDrafts((prev) => ({ ...prev, [id]: { remove: true } }));
	};

	const activeTab = TABS.find((t) => t.id === tab);
	const visibleGroups = useMemo(() => {
		if (!activeTab?.section) return [];
		const term = search.trim().toLowerCase();
		const groups = [];
		const byName = {};
		fields.filter((f) => f.section === activeTab.section).forEach((f) => {
			const id = fieldId(f.section, f.key);
			if (show === 'set' && !f.isSet) return;
			if (show === 'changed' && !changedIds.includes(id)) return;
			if (term && ![f.key, f.label, f.description, f.group].some((s) => (s || '').toLowerCase().includes(term))) return;
			if (!byName[f.group]) {
				byName[f.group] = { name: f.group, fields: [] };
				groups.push(byName[f.group]);
			}
			byName[f.group].fields.push(f);
		});
		return groups;
	}, [fields, activeTab, search, show, changedIds]);

	const changeCountBySection = useMemo(() => {
		const counts = {};
		changedIds.forEach((id) => { const s = fieldById[id]?.section; counts[s] = (counts[s] || 0) + 1; });
		return counts;
	}, [changedIds, fieldById]);

	const addCustomKey = (e) => {
		e.preventDefault();
		const key = newKey.trim();
		if (!KEY_PATTERN.test(key)) {
			notificationServices.generateNotification({ type: 'error', message: 'Use letters, digits and _ only, starting with a letter or _' });
			return;
		}
		if (fieldById[fieldId(activeTab.section, key)]) {
			notificationServices.generateNotification({ type: 'error', message: `${key} already exists - search for it below` });
			setSearch(key);
			return;
		}
		const field = {
			key, label: key, group: 'New keys', type: newKeyType, section: activeTab.section, isSet: false,
			isCustom: true, usedBy: 0, description: 'Added on this page; saved only when you save.',
		};
		setCustomFields((prev) => [...prev, field]);
		startValue(field);
		setNewKey('');
		setShow('all');
		setSearch('');
	};

	const reviewItems = changedIds.map((id) => {
		const f = fieldById[id];
		const d = drafts[id];
		return { id, field: f, draft: d };
	});

	const save = async () => {
		const changes = reviewItems.map(({ field, draft }) => {
			const change = { section: field.section, key: field.key, from: field.isSet ? field.value : null };
			if (field.section !== 'column') change.wasSet = !!field.isSet;
			if (draft.remove) change.remove = true;
			else change.to = draft.to;
			return change;
		});
		setSaving(true);
		try {
			const res = await businessConfigServices.saveBusinessConfig(customerId, changes);
			if (res?.data) {
				setData(res.data);
				setDrafts({});
				setCustomFields([]);
				setReviewOpen(false);
				notificationServices.generateNotification({ type: 'success', message: `Saved ${changes.length} change${changes.length > 1 ? 's' : ''} for ${res.data.customerName || customerId}` });
				// This console runs as this customer: refresh appConfig so the change applies here too
				if (setAppConfig) {
					authServices.fetchAppConfig().then((cfg) => {
						if (cfg?.data) {
							try { localStorage.setItem('appConfig', JSON.stringify(cfg.data)); } catch (err) { /* storage full or blocked */ }
							setAppConfig(cfg.data);
						}
					}).catch(() => {});
				}
			}
		} catch (e) {
			// handleResponse has already shown the error (e.g. someone else changed a field); keep the drafts
		} finally {
			setSaving(false);
		}
	};

	if (!userPermissions?.INTERNAL) {
		return (
			<div className={styles.page}>
				<div className={styles.empty}>Business Config is available to internal users only.</div>
			</div>
		);
	}

	const customer = customers.find((c) => c.customerId === customerId);

	return (
		<div className={styles.page}>
			<header className={styles.header}>
				<div>
					<h1>Business Config</h1>
					<p>Feature flags and settings stored in Business_Config for the customer this console is logged in for. Changes apply on the next request; apps pick them up when they reload their config.</p>
				</div>
			</header>

			{loadFailed && (
				<div className={styles.card}>
					<div className={styles.empty}>
						Couldn't load the business config. <button type='button' className={styles.link} onClick={() => load(customerId)}>Retry</button>
					</div>
				</div>
			)}

			{data && (
				<section className={styles.card}>
					<div className={styles.meta}>
						<span><strong>{data.customerName || customer?.name}</strong> (customer {data.customerId})</span>
						<span>configId {data.configId}</span>
						<span>Last updated {data.updatedAt ? data.updatedAt.replace('T', ' ') : '-'}</span>
						{loading && <span>Refreshing...</span>}
					</div>

					<div className={styles.tabs} role='tablist'>
						{TABS.map((t) => (
							<button
								key={t.id}
								type='button'
								role='tab'
								aria-selected={tab === t.id}
								className={`${styles.tab} ${tab === t.id ? styles.tabActive : ''}`}
								onClick={() => setTab(t.id)}
							>
								{t.title}
								{t.section && changeCountBySection[t.section] ? <span className={styles.badge}>{changeCountBySection[t.section]}</span> : null}
								{t.id === 'history' && data.history?.length ? <span className={styles.count}>{data.history.length}</span> : null}
							</button>
						))}
					</div>

					{activeTab?.section && (
						<>
							<div className={styles.toolbar}>
								<p className={styles.blurb}>{activeTab.blurb}</p>
								<div className={styles.filters}>
									<input
										className={styles.search}
										type='search'
										placeholder='Search key, name or description'
										value={search}
										onChange={(e) => setSearch(e.target.value)}
									/>
									{activeTab.section !== 'column' && (
										<select value={show} onChange={(e) => setShow(e.target.value)} aria-label='Show'>
											<option value='all'>All keys</option>
											<option value='set'>Set for this customer</option>
											<option value='changed'>Changed</option>
										</select>
									)}
									{activeTab.section === 'column' && (
										<select value={show === 'set' ? 'all' : show} onChange={(e) => setShow(e.target.value)} aria-label='Show'>
											<option value='all'>All columns</option>
											<option value='changed'>Changed</option>
										</select>
									)}
								</div>
							</div>

							{visibleGroups.length === 0 && <div className={styles.empty}>Nothing matches.</div>}

							{visibleGroups.map((group) => (
								<div key={group.name} className={styles.group}>
									<h3>{group.name}</h3>
									{group.fields.map((f) => {
										const id = fieldId(f.section, f.key);
										const d = drafts[id];
										const changed = changedIds.includes(id);
										const editing = f.isSet ? !d?.remove : (d && !d.remove);
										const draftValue = d && !d.remove ? d.to : f.value;
										const draftText = d && !d.remove && d.text !== undefined ? d.text : textFor(f, f.value);
										return (
											<div key={id} className={`${styles.row} ${changed ? styles.rowChanged : ''}`}>
												<div className={styles.info}>
													<div className={styles.label}>
														<label htmlFor={`bc-${f.section}-${f.key}`}>{f.label}</label>
														{f.label !== f.key && <code>{f.key}</code>}
													</div>
													{f.description && <small>{f.description}</small>}
													<small className={styles.facts}>
														{f.section !== 'column' && (customers.length
															? `Set for ${f.usedBy || 0} of ${customers.length} customers`
															: `Set for ${f.usedBy || 0} customers`)}
														{f.section !== 'column' && f.unset && ` · Not set means: ${f.unset}`}
														{f.section === 'column' && f.dbDefault != null && `DB default ${f.dbDefault}`}
														{changed && f.isSet && ` · Was: ${formatValue(f.value)}`}
													</small>
												</div>
												<div className={styles.editor}>
													{editing ? (
														<>
															<FieldEditor
																field={f}
																draftValue={draftValue}
																draftText={draftText}
																disabled={saving}
																onChange={(patch) => setDraft(f, patch)}
															/>
															{d?.error && <small className={styles.error}>{d.error}</small>}
														</>
													) : (
														<span className={styles.unset}>
															{d?.remove ? 'Will be removed' : 'Not set'}
														</span>
													)}
												</div>
												<div className={styles.rowActions}>
													{!f.isSet && !d && (
														<button type='button' className={styles.link} onClick={() => startValue(f)} disabled={saving}>Set value</button>
													)}
													{f.isSet && f.section !== 'column' && !d?.remove && (
														<button type='button' className={styles.linkDanger} onClick={() => markRemove(f)} disabled={saving}>Remove key</button>
													)}
													{d && (changed || !f.isSet || d.remove) && (
														<button type='button' className={styles.link} onClick={() => undo(f)} disabled={saving}>Undo</button>
													)}
												</div>
											</div>
										);
									})}
								</div>
							))}

							{activeTab.section !== 'column' && (
								<form className={styles.addKey} onSubmit={addCustomKey}>
									<span>Add a key that isn't listed</span>
									<input type='text' placeholder='keyName' value={newKey} onChange={(e) => setNewKey(e.target.value)} disabled={saving} />
									<select value={newKeyType} onChange={(e) => setNewKeyType(e.target.value)} disabled={saving} aria-label='Type'>
										{CUSTOM_KEY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
									</select>
									<button type='submit' className={styles.secondary} disabled={!newKey.trim() || saving}>Add</button>
								</form>
							)}
						</>
					)}

					{tab === 'history' && (
						<div className={styles.history}>
							{!data.history?.length && <div className={styles.empty}>No changes made from this page yet.</div>}
							{data.history?.map((h) => (
								<div key={h.id} className={styles.historyItem}>
									<div className={styles.historyHead}>
										<strong>{h.by ?? 'Unknown user'}</strong>
										<span>{(h.at || '').replace('T', ' ')}</span>
									</div>
									<ul>
										{(h.changes || []).map((c, i) => (
											<li key={`${c.section}-${c.key}-${i}`}>
												<code>{c.section === 'column' ? c.key : `${c.section}.${c.key}`}</code>
												{c.removed
													? <span>{` removed (was ${formatValue(c.from)})`}</span>
													: <span>{` ${formatValue(c.from, c.section === 'column' || c.wasSet !== false)} → ${formatValue(c.to)}`}</span>}
											</li>
										))}
									</ul>
								</div>
							))}
						</div>
					)}
				</section>
			)}

			{!data && !loadFailed && <div className={styles.empty}>{customerId == null ? 'Customer not found for this login.' : 'Loading...'}</div>}

			{dirty && (
				<div className={styles.saveBar}>
					<span>
						{`${changedIds.length} unsaved change${changedIds.length > 1 ? 's' : ''}`}
						{errorIds.length > 0 && <span className={styles.error}>{` · fix ${errorIds.length} invalid value${errorIds.length > 1 ? 's' : ''} first`}</span>}
					</span>
					<div>
						<button type='button' className={styles.link} onClick={() => { setDrafts({}); setCustomFields([]); }} disabled={saving}>Discard all</button>
						<button type='button' className={styles.primary} onClick={() => setReviewOpen(true)} disabled={saving || errorIds.length > 0}>Review and save</button>
					</div>
				</div>
			)}

			<Dialog open={reviewOpen} onClose={() => { if (!saving) setReviewOpen(false); }} maxWidth='sm' fullWidth>
				<DialogTitle>{`Save ${changedIds.length} change${changedIds.length > 1 ? 's' : ''} for ${data?.customerName || customerId}?`}</DialogTitle>
				<DialogContent>
					<ul className={styles.review}>
						{reviewItems.map(({ id, field, draft }) => (
							<li key={id}>
								<div>
									<strong>{field.label}</strong> <code>{field.section === 'column' ? field.key : `${field.section}.${field.key}`}</code>
								</div>
								<div className={styles.reviewValues}>
									{draft.remove
										? `Remove key (currently ${formatValue(field.value)})`
										: `${formatValue(field.value, field.isSet)} → ${formatValue(draft.to)}`}
								</div>
							</li>
						))}
					</ul>
					<p className={styles.reviewNote}>
						Takes effect on the next request for this customer. Console users see it after their console reloads its config; app users after the app does.
					</p>
				</DialogContent>
				<DialogActions>
					<button type='button' className={styles.link} onClick={() => setReviewOpen(false)} disabled={saving}>Cancel</button>
					<button type='button' className={styles.primary} onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
				</DialogActions>
			</Dialog>
		</div>
	);
}
